import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Loader from "../../../components/Loader/Loader";
import AttributionLockBadge from "../../../components/AttributionLockBadge";
import NetFeeBreakdownTooltip from "../../../components/NetFeeBreakdownTooltip";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";
import { fmt$ } from "../../../utils/format";
import { useCommissionConfig, formatCommissionPct } from "../../../hooks/useCommissionConfig";

// Base names for each commission source + the commission-config family that
// carries its live percentage. The percentage is prefixed only when config
// resolves it — otherwise the label renders WITHOUT a number rather than a
// hardcoded (and potentially wrong) 30 / 15 / 5.
const CUT_BASE_LABEL = {
  INVESTOR: "Investor",
  DEAL: "Deal",
  SUB_CONTRACTOR_OVERRIDE: "Sub-contractor override",
};
const CUT_FAMILY = {
  INVESTOR: "bc_direct",
  DEAL: "deal",
  SUB_CONTRACTOR_OVERRIDE: "bc_override",
};

function cutLabel(src, config) {
  const base = CUT_BASE_LABEL[src] || src;
  const pct = formatCommissionPct(config?.[CUT_FAMILY[src]]?.pct);
  return pct ? `${pct} ${base}` : base;
}

const STAGE_LABEL = {
  CLICK: "Clicked referral link",
  SIGNUP: "Signed up",
  KYC_COMPLETE: "Completed KYC",
  INVESTMENT: "Invested",
  DEAL_LIVE: "Deal went live",
};

const COMMISSION_STATUS_CLASS = {
  PENDING:   "s-rev",
  APPROVED:  "s-info",
  PAID:      "s-active",
  PAID_OUT:  "s-active",
  CANCELLED: "s-mute",
  VOID:      "s-mute",
  REVERSED:  "s-fail",
  FAILED:    "s-fail",
};

function userLabel(u, fallbackId) {
  if (!u) return fallbackId ? `#${fallbackId}` : "—";
  const full = [u.firstname, u.lastname].filter(Boolean).join(" ");
  const name = full || u.email || `#${u.id || fallbackId}`;
  return (
    <span>
      {name}
      <span className="mono" style={{ marginLeft: 8, color: "var(--tx-3)", fontSize: "var(--t-xs)" }}>
        #{u.id || fallbackId}
      </span>
    </span>
  );
}

const KeyVal = ({ label, children }) =>
  children !== undefined && children !== null && children !== "" ? (
    <>
      <dt
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: "var(--t-xs)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--tx-3)",
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: "0 0 12px", fontSize: "var(--t-md)", color: "var(--tx)" }}>
        {children}
      </dd>
    </>
  ) : null;

function BDCEntityDetail() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { config: commissionConfig } = useCommissionConfig();

  useEffect(() => {
    let live = true;
    setLoading(true);
    bdcApi
      .entityDetail(type, id)
      .then((res) => {
        if (!live) return;
        setDetail(res?.data || null);
        setError(null);
      })
      .catch((err) => {
        if (!live) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [type, id]);

  const summary = detail?.summary;
  const attribution = detail?.attribution;
  const lines = detail?.commission_lines || [];
  const events = detail?.events || [];

  const consultantName = attribution?.Consultant
    ? [attribution.Consultant.firstname, attribution.Consultant.lastname].filter(Boolean).join(" ") || attribution.Consultant.email
    : null;

  // Backend hydrates `attribution.Entity` for INVESTOR (user) and DEAL
  // (campaign) so the page can lead with a recognizable name; CONSULTANT
  // already has `Consultant`.
  const entityType = String(type || "").toUpperCase();
  const entityName = (() => {
    if (entityType === "CONSULTANT") return consultantName;
    const e = attribution?.Entity;
    if (!e) return null;
    if (entityType === "DEAL") {
      return e.campaign_name || e.corporation_legal_name || null;
    }
    const full = [e.firstname, e.lastname].filter(Boolean).join(" ");
    return full || e.email || null;
  })();
  const entityEmail = entityType === "CONSULTANT"
    ? attribution?.Consultant?.email
    : attribution?.Entity?.email;

  const idTag = `${entityType} #${id}`;
  const title = entityName || idTag;
  const subtitle = entityName ? (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "baseline" }}>
      {entityEmail && <span>{entityEmail}</span>}
      <span className="mono" style={{ color: "var(--tx-3)", fontSize: "var(--t-xs)" }}>
        {idTag}
      </span>
    </span>
  ) : null;

  const headActions = (
    <>
      <Btn variant="ghost" size="sm" onClick={() => navigate("/bdc/network")}>
        <ChevronLeft /> Network
      </Btn>
      {attribution && (
        <AttributionLockBadge
          lockedAt={attribution.locked_at}
          lockedBy={attribution.unlocked_by_admin_id}
        />
      )}
    </>
  );

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title={title} subtitle={subtitle} actions={headActions} />

      {error && <Alert tone="danger">{error}</Alert>}

      {!loading && (
        <>
          {attribution && (
            <section className="card">
              <div className="card__head">
                <div className="card__title">
                  {attribution.entity_type === "CONSULTANT" ? "Sub-contractor" : "Attribution"}
                </div>
              </div>
              <div className="card__body">
                <dl style={{ margin: 0 }}>
                  {entityType !== "CONSULTANT" && entityName && (
                    <KeyVal label={entityType === "DEAL" ? "Deal" : "Investor"}>
                      <span>{entityName}</span>
                      <span className="mono" style={{ marginLeft: 8, color: "var(--tx-3)", fontSize: "var(--t-xs)" }}>
                        #{attribution.entity_id}
                      </span>
                    </KeyVal>
                  )}
                  {entityType !== "CONSULTANT" && entityEmail && (
                    <KeyVal label="Email">{entityEmail}</KeyVal>
                  )}
                  {attribution.Consultant && (
                    <KeyVal label="Email">{attribution.Consultant.email}</KeyVal>
                  )}
                  <KeyVal label="Role">{attribution.role_type}</KeyVal>
                  {attribution.commission_override_pct !== undefined && attribution.commission_override_pct !== null && (
                    <KeyVal label="Commission override">
                      {formatCommissionPct(attribution.commission_override_pct)}
                    </KeyVal>
                  )}
                  {attribution.is_active !== undefined && (
                    <KeyVal label="Active">{attribution.is_active ? "Yes" : "No"}</KeyVal>
                  )}
                  <KeyVal label="Source"><Pill tone="blue">{attribution.source}</Pill></KeyVal>
                  <KeyVal label="Attributed BDC">{userLabel(attribution.AttributedBdc, attribution.attributed_bdc_id)}</KeyVal>
                  {attribution.sub_contractor_bdc_id && (
                    <KeyVal label="Sub-contractor sponsor">{userLabel(attribution.SubContractorBdc, attribution.sub_contractor_bdc_id)}</KeyVal>
                  )}
                  <KeyVal label="Attribution row created">
                    {formatAuditTimestamp(attribution.createdAt)}
                  </KeyVal>
                  {attribution.locked_at && (
                    <KeyVal label="Secured at">{formatAuditTimestamp(attribution.locked_at)}</KeyVal>
                  )}
                </dl>
              </div>
            </section>
          )}

          {summary && (
            <section className="card">
              <div className="card__head">
                <div className="card__title">Your cut</div>
              </div>
              <div className="card__body card__body--flush">
                <div
                  className="kpi-rail"
                  style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}
                >
                  {/* Per-currency totals (never a cross-FX sum); the per-source
                      split renders only when all lines share one currency. */}
                  {Object.entries(summary.commission_totals_by_currency || {}).map(([cur, total]) => (
                    <div key={cur} className="kpi">
                      <div className="kpi__lbl">Commission ({cur})</div>
                      <div className="kpi__num">{fmt$(total, 2, cur)}</div>
                    </div>
                  ))}
                  {Object.keys(summary.commission_totals_by_currency || {}).length <= 1 &&
                    Object.entries(summary.commission_totals || {}).map(([src, total]) => (
                      <div key={src} className="kpi">
                        <div className="kpi__lbl">{cutLabel(src, commissionConfig)}</div>
                        <div className="kpi__num">
                          {fmt$(total, 2, Object.keys(summary.commission_totals_by_currency || {})[0])}
                        </div>
                      </div>
                    ))}
                  <div className="kpi kpi--info">
                    <div className="kpi__lbl">Total net fees</div>
                    <div className="kpi__num">{fmt$(summary.net_fee_total, 2)}</div>
                    <div className="kpi__meta">
                      <span className="kpi__period">
                        gross {fmt$(summary.gross_fee_total, 2)} · {summary.line_count} line
                        {summary.line_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="card">
            <div className="card__head">
              <div className="card__title">Commission lines</div>
            </div>
            <div className="card__body card__body--flush">
              {lines.length === 0 ? (
                <Empty>No commission lines yet.</Empty>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Source</th>
                      {/* Principal-based v2 lines write net_fee = 0 by design;
                          the honest basis is basis_amount × rate. */}
                      <th className="r">Basis</th>
                      <th className="r">Rate</th>
                      <th className="r">Amount</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const statusKey = String(l.status || "").toUpperCase();
                      const statusClass = COMMISSION_STATUS_CLASS[statusKey] || "s-mute";
                      const cur = String(l.basis_currency || "").trim().toUpperCase() || "CAD";
                      const hasBasis = l.basis_amount != null;
                      return (
                        <tr key={l.id}>
                          <td><span className="tbl-id">#{l.id}</span></td>
                          <td>{l.source_type}</td>
                          <td className="r">
                            {hasBasis ? (
                              <>
                                {fmt$(l.basis_amount, 2, cur)}
                                <span className="muted"> {l.basis_type || ""}</span>
                              </>
                            ) : (
                              <NetFeeBreakdownTooltip
                                grossFee={l.gross_fee}
                                refunds={l.refunds}
                                platformCost={l.platform_cost}
                                platformCostPct={l.platform_cost_pct}
                                netFee={l.net_fee}
                              >
                                {fmt$(l.net_fee, 2, cur)}
                              </NetFeeBreakdownTooltip>
                            )}
                          </td>
                          <td className="r">{formatCommissionPct(l.commission_pct)}</td>
                          <td className="r">
                            <strong>{fmt$(l.commission_amount, 2, cur)}</strong>
                          </td>
                          <td>
                            <span className={`tbl-status ${statusClass}`}>{l.status}</span>
                          </td>
                          <td className="muted">{formatAuditTimestamp(l.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">Timeline</div>
            </div>
            <div className="card__body">
              {events.length === 0 ? (
                <Empty>No conversion events recorded for this entity yet.</Empty>
              ) : (
                <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  {events.map((ev) => (
                    <li
                      key={ev.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        alignItems: "baseline",
                        gap: 12,
                        padding: "8px 0",
                        borderBottom: "1px solid var(--bd)",
                      }}
                    >
                      <Pill tone="blue">{ev.event_type}</Pill>
                      <div style={{ color: "var(--tx)", fontSize: "var(--t-md)" }}>
                        {STAGE_LABEL[ev.event_type] || ev.event_type}
                        {ev.utm_source && (
                          <span style={{ color: "var(--tx-3)", fontFamily: "var(--f-mono)", fontSize: "var(--t-xs)", marginLeft: 8 }}>
                            utm_source: {ev.utm_source}
                          </span>
                        )}
                      </div>
                      <div className="mono" style={{ fontSize: "var(--t-xs)", color: "var(--tx-3)" }}>
                        {formatAuditTimestamp(ev.event_at)}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

export default BDCEntityDetail;
